import React, {
	CSSProperties,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import './TemplateOne.css';
import { BASE_MEDIA_ASSETS } from './config';
import Countdown from '../components/countdown/Countdown';
import Coupon from '../components/coupon/Coupon';
import CtaButton from '../components/cta-button/CtaButton';
import TextBlock from '../components/text/TextBlock';
import {
	getHoverProps,
	getImageStyle,
	getPaddingStyle,
	px,
} from '../components/styles';
import { resolveText } from '../index';
import { resolveDesignAt } from '../responsive';
import { useBannerCountdown } from '../../../../hooks/useBannerCountdown';
import { cssUrl, formatUnit } from '../../stickyBanner.helper';
import { MediaKey, SiteTimezone, TemplateProps } from '../../types';

const DEFAULT_SITE_TIMEZONE: SiteTimezone = { timezone: '', gmtOffset: 0 };

/**
 * Base layout every template uses. Components sit in fixed positions;
 * hidden components are left out and the others close the gap.
 */
const TemplateOne: React.FC< TemplateProps > = ( {
	design: savedDesign,
	content,
	countdown,
	siteTimezone = DEFAULT_SITE_TIMEZONE,
	isPreview = false,
	onClose,
} ) => {
	const [ isClosed, setIsClosed ] = useState( false );
	const { target, timeLeft } = useBannerCountdown( countdown, siteTimezone );

	// Responsive: the banner's own width picks the per-width changes
	// (1366, 1280, 1024, 768, 640 and narrower), measured before paint.
	const sectionRef = useRef< HTMLElement >( null );
	const [ width, setWidth ] = useState( 0 );
	useLayoutEffect( () => {
		const section = sectionRef.current;
		if ( ! section ) {
			return;
		}
		const update = () => setWidth( section.offsetWidth );
		update();
		if ( typeof ResizeObserver === 'undefined' ) {
			window.addEventListener( 'resize', update );
			return () => window.removeEventListener( 'resize', update );
		}
		const observer = new ResizeObserver( update );
		observer.observe( section );
		return () => observer.disconnect();
	}, [] );
	const design = useMemo(
		// Before the first measure (0) the desktop design is used.
		() =>
			width > 0 ? resolveDesignAt( savedDesign, width ) : savedDesign,
		[ savedDesign, width ]
	);

	const { components, media } = design;
	const text = ( key: Parameters< typeof resolveText >[ 2 ] ) =>
		resolveText( design, content, key );

	// The countdown shows when the template has it and the banner has an end
	// date (the admin preview shows zeros instead).
	const hasCountdown = components.countdown.visible;
	const showCountdown = hasCountdown && ( isPreview || target !== null );

	// On the live site an expired countdown can remove the banner entirely.
	if (
		! isPreview &&
		hasCountdown &&
		timeLeft.expired &&
		countdown.afterExpiry === 'hide'
	) {
		return null;
	}

	const resolveImage = ( key: MediaKey ): string => {
		const item = media[ key ];
		if ( ! item || ! item.isVisible ) {
			return '';
		}
		return item.url || BASE_MEDIA_ASSETS[ key ] || '';
	};

	/* ----- Background ----- */
	const background = components.background;
	const sectionStyle: CSSProperties = {
		minHeight: px( background.style.height ),
		borderRadius: formatUnit( media.bgImage.borderRadius ),
	};
	if ( background.variant === 'gradient' ) {
		sectionStyle.background = `linear-gradient(${
			Number( background.style.gradientAngle ) || 0
		}deg, ${ background.style.gradientFrom } 0%, ${
			background.style.gradientTo
		} 100%)`;
	} else {
		sectionStyle.backgroundColor = background.style.color;
		const bgImage =
			background.variant === 'image' ? resolveImage( 'bgImage' ) : '';
		if ( bgImage ) {
			const width = formatUnit( media.bgImage.width );
			const height = formatUnit( media.bgImage.height );
			sectionStyle.backgroundImage = cssUrl( bgImage );
			if ( width || height ) {
				sectionStyle.backgroundSize = `${ width || 'auto' } ${
					height || 'auto'
				}`;
			}
		}
	}

	const leftImage = components.leftImage.visible
		? resolveImage( 'leftIcon' )
		: '';
	const rightImage = components.rightImage.visible
		? resolveImage( 'rightIcon' )
		: '';

	// Content padding and the space between items (rows have their own
	// gap in CSS when items wrap on small screens).
	const contentStyle: CSSProperties = {
		...getPaddingStyle( background.style ),
		columnGap: px( background.style.gap ),
	};

	// First to fourth text, left to right.
	const textParts = [
		{ key: 'preText' as const, settings: components.preText },
		{ key: 'extraText' as const, settings: components.text },
		{ key: 'highlight' as const, settings: components.headline },
		{ key: 'secondary' as const, settings: components.fourthText },
	];

	const closeStyle = components.closeButton.style;
	const closeHover = getHoverProps( closeStyle, [ 'hoverText' ] );
	const closeSize = px( closeStyle.size ) || '14px';

	return (
		<section
			ref={ sectionRef }
			className={ `template-one${ isClosed ? ' temp-hidden' : '' }` }
			style={ sectionStyle }
			aria-hidden={ isClosed }
		>
			{ leftImage && (
				<div
					className="temp-side is-left"
					style={ { left: px( components.leftImage.style.offset ) } }
				>
					<img
						src={ leftImage }
						alt=""
						style={ getImageStyle( media.leftIcon ) }
					/>
				</div>
			) }

			<div className="temp-content" style={ contentStyle }>
				{ textParts.some( ( part ) => part.settings.visible ) && (
					// The four texts share one group (they wrap together);
					// "Space between texts" is set on the background.
					<div
						className="temp-text-group"
						style={ {
							gap:
								px( background.style.textGap ) ||
								px( components.headline.style.gap ),
						} }
					>
						{ textParts.map(
							( part ) =>
								part.settings.visible && (
									<TextBlock
										key={ part.key }
										settings={ part.settings }
										text={ text( part.key ) }
										isPreview={ isPreview }
									/>
								)
						) }
					</div>
				) }

				{ components.coupon.visible && (
					<Coupon
						settings={ components.coupon }
						label={ text( 'couponCopyLabel' ) }
						code={ text( 'couponCode' ) }
						copiedText={ text( 'couponCopiedText' ) }
						copyButton={ text( 'couponCopyButton' ) }
						ticket={ media.couponBg }
						ticketUrl={ resolveImage( 'couponBg' ) }
						boxImage={ media.couponBoxBg }
						boxImageUrl={ resolveImage( 'couponBoxBg' ) }
					/>
				) }

				{ showCountdown && (
					<Countdown
						settings={ components.countdown }
						label={ text( 'countdownLabel' ) }
						digits={ text( 'countdownDigits' ) }
						timeLeft={ timeLeft }
					/>
				) }

				{ components.cta.visible && (
					<CtaButton
						settings={ components.cta }
						text={ text( 'cta' ) }
						link={ content.ctaLink }
						openInNewTab={ content.ctaNewTab }
						isPreview={ isPreview }
					/>
				) }
			</div>

			{ rightImage && (
				<div
					className="temp-side is-right"
					style={ {
						right: px( components.rightImage.style.offset ),
					} }
				>
					<img
						src={ rightImage }
						alt=""
						style={ getImageStyle( media.rightIcon ) }
					/>
				</div>
			) }

			{ components.closeButton.visible && (
				<button
					type="button"
					className={ `temp-close${ closeHover.className }` }
					onClick={ () => {
						if ( isPreview ) {
							return;
						}
						setIsClosed( true );
						onClose?.();
					} }
					aria-label="Close banner"
					title={
						isPreview ? 'Close is disabled in preview' : 'Close'
					}
					style={ {
						color: closeStyle.color,
						...getPaddingStyle( closeStyle ),
						...closeHover.style,
					} }
				>
					<svg
						width={ closeSize }
						height={ closeSize }
						viewBox="0 0 14 14"
						aria-hidden
						focusable="false"
					>
						<path
							fill="currentColor"
							d="M12.2049 13.6919C12.6156 14.1026 13.2814 14.1026 13.692 13.6919C14.1027 13.2813 14.1027 12.6155 13.692 12.2048L8.48714 6.99999L13.692 1.79516C14.1027 1.38451 14.1027 0.718705 13.692 0.308052C13.2813 -0.102601 12.6155 -0.102601 12.2049 0.308052L7.00003 5.51289L1.79512 0.30799C1.38446 -0.102664 0.718658 -0.102663 0.308003 0.30799C-0.102651 0.718643 -0.102652 1.38444 0.308003 1.7951L5.51292 6.99999L0.307991 12.2049C-0.102664 12.6156 -0.102663 13.2814 0.307991 13.692C0.718646 14.1027 1.38445 14.1027 1.7951 13.692L7.00003 8.4871L12.2049 13.6919Z"
						/>
					</svg>
				</button>
			) }
		</section>
	);
};

export default TemplateOne;
